import React from 'react';

// Local, dependency-free reimplementations of the handful of recompose helpers
// this codebase actually uses. recompose is unmaintained and pulls a vulnerable
// fbjs -> node-fetch chain, so it has been removed in favour of these.

export const compose = (...funcs) =>
    funcs.reduce(
        (a, b) => (...args) => a(b(...args)),
        (arg) => arg,
    );

export const getDisplayName = (Component) => {
    if (typeof Component === 'string') {
        return Component;
    }
    if (!Component) {
        return undefined;
    }
    return Component.displayName || Component.name || 'Component';
};

export const wrapDisplayName = (BaseComponent, hocName) =>
    `${hocName}(${getDisplayName(BaseComponent)})`;

export const setDisplayName = (displayName) => (BaseComponent) => {
    BaseComponent.displayName = displayName;
    return BaseComponent;
};

export const nest = (...Components) => {
    const Nest = ({ children, ...props }) =>
        Components.reduceRight(
            (child, Component) => React.createElement(Component, props, child),
            children,
        );
    Nest.displayName = `nest(${Components.map(getDisplayName).join(', ')})`;
    return Nest;
};

export const withProps = (input) => (BaseComponent) => {
    const WithProps = (props) =>
        React.createElement(BaseComponent, {
            ...props,
            ...(typeof input === 'function' ? input(props) : input),
        });
    WithProps.displayName = wrapDisplayName(BaseComponent, 'withProps');
    return WithProps;
};

export const renderComponent = (Component) => () => {
    const RenderComponent = (props) => React.createElement(Component, props);
    RenderComponent.displayName = wrapDisplayName(Component, 'renderComponent');
    return RenderComponent;
};

export const branch = (test, left, right = (x) => x) => (BaseComponent) => {
    // Memoise each branch's component type so it is created at most once, rather
    // than on every render — recreating the type each render would force React to
    // unmount/remount the subtree (state loss + extra lifecycle calls).
    let leftComponent;
    let rightComponent;
    const Branch = (props) => {
        if (test(props)) {
            leftComponent = leftComponent || left(BaseComponent);
            return React.createElement(leftComponent, props);
        }
        rightComponent = rightComponent || right(BaseComponent);
        return React.createElement(rightComponent, props);
    };
    Branch.displayName = wrapDisplayName(BaseComponent, 'branch');
    return Branch;
};

export const lifecycle = (spec) => (BaseComponent) => {
    class Lifecycle extends React.Component {
        render() {
            return React.createElement(BaseComponent, { ...this.props, ...this.state });
        }
    }
    Object.keys(spec).forEach((key) => {
        Lifecycle.prototype[key] = spec[key];
    });
    Lifecycle.displayName = wrapDisplayName(BaseComponent, 'lifecycle');
    return Lifecycle;
};
